import {
  candidates,
  conflicts,
  isComplete,
  isValid,
  peers,
  type Board,
} from '../sudoku';
import {
  STRIKE_LIMIT,
  type Action,
  type GameState,
  type GameStatus,
  type InputConfig,
  type Snapshot,
} from './types';

const cell = (arr: number[], i: number): number => arr[i] ?? 0;
const noteAt = (notes: number[][], i: number): number[] => notes[i] ?? [];

function snapshot(s: GameState): Snapshot {
  return { cells: [...s.cells], notes: s.notes.map((n) => [...n]) };
}

/** Record the pre-mutation state for undo and drop the redo stack. */
function commit(prev: GameState, next: Partial<GameState>): GameState {
  return { ...prev, ...next, past: [...prev.past, snapshot(prev)], future: [] };
}

function withCompletion(state: GameState, cells: Board, mistakes: number): {
  status: GameStatus;
  running: boolean;
  mistakes: number;
} {
  let status: GameStatus = 'playing';
  if (mistakes >= STRIKE_LIMIT) status = 'failed';
  else if (isComplete(cells) && isValid(cells)) status = 'solved';
  return { status, running: status === 'playing' && state.running, mistakes };
}

function placeValue(
  state: GameState,
  index: number,
  value: number,
  config: InputConfig,
  isUserInput: boolean,
): GameState {
  const { autoRemoveNotes, mistakeMode } = config;
  const cells = [...state.cells];

  // Tapping the digit already in the cell clears it (common mobile UX).
  if (cell(cells, index) === value && isUserInput) {
    cells[index] = 0;
    const notes = state.notes.map((n) => [...n]);
    return commit(state, { cells, notes });
  }

  cells[index] = value;
  const notes = state.notes.map((n) => [...n]);
  notes[index] = [];
  if (autoRemoveNotes) {
    for (const p of peers[index] ?? []) {
      const list = noteAt(notes, p);
      if (list.includes(value)) notes[p] = list.filter((v) => v !== value);
    }
  }

  let mistakes = state.mistakes;
  if (mistakeMode === 'strike' && conflicts(cells, index).length > 0) mistakes += 1;

  return commit(state, { cells, notes, ...withCompletion(state, cells, mistakes) });
}

function toggleNote(state: GameState, index: number, value: number): GameState {
  if (cell(state.cells, index) !== 0) return state; // notes only on empty cells
  const notes = state.notes.map((n) => [...n]);
  const list = noteAt(notes, index);
  notes[index] = list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value].sort((a, b) => a - b);
  return commit(state, { notes });
}

function moveSelection(state: GameState, dir: 'up' | 'down' | 'left' | 'right'): GameState {
  const cur = state.selected ?? 0;
  const row = Math.floor(cur / 9);
  const col = cur % 9;
  const next = {
    up: [Math.max(0, row - 1), col],
    down: [Math.min(8, row + 1), col],
    left: [row, Math.max(0, col - 1)],
    right: [row, Math.min(8, col + 1)],
  }[dir];
  return { ...state, selected: next[0]! * 9 + next[1]! };
}

export function reducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case 'select':
      return { ...state, selected: action.index };

    case 'move':
      return moveSelection(state, action.dir);

    case 'input': {
      const i = state.selected;
      if (i === null || state.status !== 'playing' || cell(state.givens, i) !== 0) return state;
      return state.notesMode
        ? toggleNote(state, i, action.value)
        : placeValue(state, i, action.value, action.config, true);
    }

    case 'placeHint':
      if (state.status !== 'playing' || cell(state.givens, action.index) !== 0) return state;
      return placeValue(
        { ...state, selected: action.index },
        action.index,
        action.value,
        action.config,
        false,
      );

    case 'erase': {
      const i = state.selected;
      if (i === null || state.status !== 'playing' || cell(state.givens, i) !== 0) return state;
      if (cell(state.cells, i) === 0 && noteAt(state.notes, i).length === 0) return state;
      const cells = [...state.cells];
      cells[i] = 0;
      const notes = state.notes.map((n) => [...n]);
      notes[i] = [];
      return commit(state, { cells, notes });
    }

    case 'toggleNotesMode':
      return { ...state, notesMode: !state.notesMode };

    case 'autoNotes': {
      const cand = candidates(state.cells);
      const filled = state.cells.map((v, i) =>
        v === 0 ? [...(cand[i] ?? new Set<number>())].sort((a, b) => a - b) : [],
      );
      // Toggle: if notes already match the computed candidates, clear them;
      // otherwise fill. Compares actual state so undo/redo stays consistent.
      const alreadyFilled = filled.every(
        (list, i) =>
          list.length === noteAt(state.notes, i).length &&
          list.every((v, j) => v === noteAt(state.notes, i)[j]),
      );
      const notes = alreadyFilled ? state.cells.map(() => []) : filled;
      return commit(state, { notes });
    }

    case 'undo': {
      const prev = state.past[state.past.length - 1];
      if (!prev) return state;
      return {
        ...state,
        cells: prev.cells,
        notes: prev.notes,
        past: state.past.slice(0, -1),
        future: [snapshot(state), ...state.future],
        status: 'playing',
      };
    }

    case 'redo': {
      const next = state.future[0];
      if (!next) return state;
      return {
        ...state,
        cells: next.cells,
        notes: next.notes,
        past: [...state.past, snapshot(state)],
        future: state.future.slice(1),
        status: 'playing',
      };
    }

    case 'tick':
      return state.running ? { ...state, elapsedMs: state.elapsedMs + action.ms } : state;

    case 'setRunning':
      return { ...state, running: action.running && state.status === 'playing' };

    case 'useHint':
      return { ...state, hintsUsed: state.hintsUsed + 1 };

    default:
      return state;
  }
}
